import { useEffect, useState } from "react";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import KpiTile from "@/components/KpiTile";
import Callout from "@/components/Callout";
import { fmtMoney, fmtNumber, fmtPercent, fmtMultiple } from "@/lib/format";
import { DASHBOARD } from "@/constants/testIds";
import { ArrowUpRight, ArrowDownRight, Minus, CheckCircle2, AlertTriangle } from "lucide-react";
import ReconcileFlag, { findReconcile } from "@/components/ReconcileFlag";
import Sparkline from "@/components/Sparkline";
import SourceBadge from "@/components/SourceBadge";
import LeadSourcesSection from "@/components/LeadSourcesSection";
import { TrendingUp, TrendingDown } from "lucide-react";

/**
 * MonthTile — a KPI tile for the "last completed month" Dashboard framing.
 * Shows: last-full-month value · source badge · MoM delta chip · YoY badge
 * · trend line with min/max Y and first/last X ticks. The current partial
 * month is filtered out of the series so nothing on the tile reflects
 * unfinished data.
 */
function MonthTile({ testid, label, value, source, monthName, momPct,
                     yoyPct, invertDelta = false, spark, sparkStroke,
                     sparkInvert = false, hint, yFormat }) {
  const cleanSpark = (spark || []).filter((r) => !r.is_partial);
  return (
    <div className="kpi-tile" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-[0.2em] opacity-70">{label}</div>
        {source && <SourceBadge {...source} testid={`${testid}-source`} />}
      </div>
      <div className="text-3xl font-bold text-navy mt-2">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-widest opacity-60">
        {monthName ? `${monthName} · full month` : "Last full month"}
        {hint && <span className="ml-2 opacity-70 normal-case tracking-normal italic">{hint}</span>}
      </div>
      <div className="mt-2 flex items-center gap-3">
        {momPct != null ? (
          <MomChip pct={momPct} invert={invertDelta} testid={`${testid}-mom`} />
        ) : (
          <span className="text-[10px] uppercase tracking-widest opacity-40" data-testid={`${testid}-mom-none`}>
            MoM unavailable
          </span>
        )}
        {yoyPct != null && <YoyBadge pct={yoyPct} invert={invertDelta} />}
      </div>
      {cleanSpark.length > 0 && (
        <div className="mt-3" data-testid={`${testid}-spark`}>
          <Sparkline series={cleanSpark} variant="line" invertDelta={sparkInvert}
            stroke={sparkStroke || "#1D57A5"} ytdStroke={sparkStroke || "#1D57A5"}
            height={80} yFormat={yFormat} />
        </div>
      )}
    </div>
  );
}

/**
 * MomChip — Month-over-Month delta chip. Big & obvious, matches the tone of
 * the tile it lives on (green = improvement, red = regression, grey = flat).
 * `invert=true` for metrics where lower is better (CPL, CAC).
 */
function MomChip({ pct, invert, testid }) {
  const isUp = pct > 0.5, isDown = pct < -0.5;
  const isGood = invert ? isDown : isUp;
  const isBad = invert ? isUp : isDown;
  const color = isGood ? "text-evergreen bg-evergreen/10"
    : isBad ? "text-red-700 bg-red-700/10"
    : "text-midnight bg-midnight/10 opacity-70";
  const Icon = pct > 0 ? ArrowUpRight : pct < 0 ? ArrowDownRight : Minus;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest ${color}`}
         data-testid={testid}>
      <span className="opacity-70">MoM</span>
      <Icon className="w-3 h-3" />
      {pct > 0 ? "+" : ""}{pct}%
    </div>
  );
}

function YoyBadge({ pct, invert }) {
  const isUp = pct > 0.5, isDown = pct < -0.5;
  const isGood = invert ? isDown : isUp;
  const isBad = invert ? isUp : isDown;
  const color = isGood ? "text-evergreen" : isBad ? "text-red-700" : "text-midnight opacity-60";
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  return (
    <div className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest ${color}`}>
      <span className="opacity-60 tracking-widest">YoY</span>
      <Icon className="w-3 h-3" />
      {pct > 0 ? "+" : ""}{pct}%
    </div>
  );
}

/**
 * PlatformMetricRow — one row inside a split-platform tile. Renders:
 *   label · big value (formatted) · MoM chip · 12-month bar spark · sub-line
 * `metricKey` picks which metric block on the platform ("spend" | "cpl" | "roas").
 * `format` formats the big value (money, roas×, etc.).
 * `invertDelta` = true for metrics where lower is better (CPL).
 * `subLine` is optional context text.
 * The current partial month is stripped from the sparkline.
 */
function PlatformMetricRow({ label, dot, metricKey, platform, format, invertDelta,
                             subLine, testidPrefix, yFormat }) {
  const block = platform?.[metricKey];
  const val = block?.value;
  const cleanSeries = (block?.series || []).filter((r) => !r.is_partial);
  return (
    <div className="mt-3" data-testid={testidPrefix}>
      <div className="flex items-baseline gap-2">
        <span className="inline-block w-2.5 h-2.5" style={{ background: dot }} />
        <span className="text-[11px] uppercase tracking-widest opacity-70">{label}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-3 flex-wrap">
        <div className="text-2xl font-bold text-navy" data-testid={`${testidPrefix}-value`}>
          {val != null ? format(val) : "—"}
        </div>
        {block?.delta_pct_mom != null ? (
          <MomChip pct={block.delta_pct_mom} invert={invertDelta} testid={`${testidPrefix}-mom`} />
        ) : (
          <span className="text-[10px] uppercase tracking-widest opacity-40">MoM unavailable</span>
        )}
        {block?.delta_pct_yoy != null && (
          <YoyBadge pct={block.delta_pct_yoy} invert={invertDelta} />
        )}
      </div>
      {cleanSeries.length > 0 && (
        <div className="mt-1" data-testid={`${testidPrefix}-spark`}>
          <Sparkline series={cleanSeries} variant="line" invertDelta={invertDelta}
            stroke={dot} ytdStroke={dot} height={60} yFormat={yFormat} />
        </div>
      )}
      {subLine && (
        <div className="mt-1 text-[9px] uppercase tracking-widest opacity-50">{subLine}</div>
      )}
    </div>
  );
}

/**
 * PlatformSplitTile — a KPI tile with Google Ads + Meta Ads rows for a
 * single metric (spend, cpl, roas). Data comes from
 * /api/analytics/platform-roas-monthly which returns per-platform
 * spend/cpl/roas each with {value, delta_pct_mom, series}.
 */
function PlatformSplitTile({ testid, title, monthName, platformRoas, metricKey,
                             format, invertDelta = false, source, subLineFor, yFormat }) {
  const g = platformRoas?.google;
  const m = platformRoas?.meta;
  const gSub = subLineFor ? subLineFor(g) : null;
  const mSub = subLineFor ? subLineFor(m) : null;
  return (
    <div className="kpi-tile" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-[0.2em] opacity-70">{title}</div>
        {source && <SourceBadge {...source} testid={`${testid}-source`} />}
      </div>
      <div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">
        {monthName ? `${monthName} · full month` : "Last full month"}
        {invertDelta && <span className="ml-2 opacity-70 normal-case tracking-normal italic">lower is better</span>}
      </div>
      <PlatformMetricRow label="Google Ads" dot="#1D57A5" platform={g}
        metricKey={metricKey} format={format} invertDelta={invertDelta}
        subLine={gSub} testidPrefix={`${testid}-google`} yFormat={yFormat} />
      <PlatformMetricRow label="Meta Ads"   dot="#F58518" platform={m}
        metricKey={metricKey} format={format} invertDelta={invertDelta}
        subLine={mSub} testidPrefix={`${testid}-meta`} yFormat={yFormat} />
    </div>
  );
}

import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line,
} from "recharts";

// Distinct, color-blind-safe palette (Tableau 10 / Color Universal Design inspired).
// Order is deliberate — biggest expected source first. Stable per-source mapping
// below ensures the same source always gets the same color across refreshes.
const PALETTE = [
  "#1D57A5", // cobalt blue — Google Ads
  "#F58518", // warm orange — Meta / FB Ads (neutral, not red)
  "#54A24B", // leaf green — Referral
  "#B279A2", // muted purple — Other / ChatGPT
  "#9C755F", // brown — Alumni
  "#17BECF", // teal — Organic
  "#BAB0AC", // neutral grey — fallback
];

// Stable hash of a source name to a palette index so the colors don't shuffle
// when a slice's rank changes between refreshes.
function colorFor(source, fallbackIdx) {
  if (!source) return PALETTE[fallbackIdx % PALETTE.length];
  const s = source.toLowerCase();
  if (s.startsWith("google")) return PALETTE[0];
  if (s.startsWith("meta") || s.startsWith("fb") || s.startsWith("facebook") || s.includes("webinar")) return PALETTE[1];
  if (s.startsWith("referral")) return PALETTE[2];
  if (s.startsWith("other") || s.startsWith("chatgpt") || s.includes("social")) return PALETTE[3];
  if (s.startsWith("alumni")) return PALETTE[4];
  if (s.startsWith("organic") || s.includes("knows joey") || s.includes("direct")) return PALETTE[5];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function DeltaArrow({ value, invert = false }) {
  if (value == null || value === 0) return <Minus className="w-3.5 h-3.5 inline opacity-50" />;
  const isPositive = invert ? value < 0 : value > 0;
  const Icon = value > 0 ? ArrowUpRight : ArrowDownRight;
  return <Icon className={`w-3.5 h-3.5 inline ${isPositive ? "text-evergreen" : "text-red-700"}`} />;
}

function DeltaInline({ d, formatter, invert }) {
  if (!d || d.delta == null) return <span className="opacity-50 text-xs">—</span>;
  const isPositive = invert ? d.delta < 0 : d.delta > 0;
  const color = d.delta === 0 ? "opacity-50" : isPositive ? "text-evergreen" : "text-red-700";
  return (
    <span className={`text-xs ${color} inline-flex items-center gap-0.5`}>
      <DeltaArrow value={d.delta} invert={invert} />
      {d.delta > 0 ? "+" : ""}{formatter(d.delta)}
      {d.pct != null && <span className="opacity-70 ml-0.5">({d.delta > 0 ? "+" : ""}{(d.pct * 100).toFixed(0)}%)</span>}
    </span>
  );
}

export default function RearviewPage() {
  const [dash, setDash] = useState(null);
  const [spark, setSpark] = useState(null);
  const [platformRoas, setPlatformRoas] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/analytics/dashboard`),
      api.get(`/analytics/dashboard-sparklines?grain=month&months=24`),
      api.get(`/analytics/platform-roas-monthly?months=13`),
    ]).then(([d, s, pr]) => {
      setDash(d.data);
      setSpark(s.data?.kpis || null);
      setPlatformRoas(pr.data);
    });
  }, []);

  if (!dash) return <div className="p-10 text-navy">Loading dashboard…</div>;

  // ==================================================================
  // Last completed month (LFM) framing — replaces the previous YTD tiles.
  // Each tile shows the LFM value (June, if we're in July) with a MoM delta
  // vs the prior full month (May), plus a 12-month bar sparkline for context.
  // Source: /analytics/dashboard-sparklines already surfaces:
  //   kpis[k].value           = last full month value
  //   kpis[k].delta_pct_mom   = MoM % delta
  //   kpis[k].delta_pct_yoy   = YoY % delta (null until prior-year data lands)
  //   kpis[k].series          = 12+ month bar series
  // ==================================================================
  const now = new Date();
  const currentYear = now.getFullYear();
  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  // The backend's headline_period is "YYYY-MM" for the last FULL month.
  const headlinePeriod = spark?.spend?.series?.slice().reverse().find(r => !r.is_partial)?.period || null;
  const lastFullMonthName = headlinePeriod
    ? monthNames[parseInt(headlinePeriod.slice(5), 10) - 1] : null;
  const lastFullMonthLabel = headlinePeriod
    ? `${lastFullMonthName} ${headlinePeriod.slice(0, 4)}` : null;

  const lfm = {
    spend:   spark?.spend?.value,
    leads:   spark?.leads?.value,
    cpl:     spark?.cpl?.value,
    deals:   spark?.deals?.value,
    revenue: spark?.revenue?.value,
  };
  const mom = {
    spend:   spark?.spend?.delta_pct_mom,
    leads:   spark?.leads?.delta_pct_mom,
    cpl:     spark?.cpl?.delta_pct_mom,
    deals:   spark?.deals?.delta_pct_mom,
    revenue: spark?.revenue?.delta_pct_mom,
  };
  const yoy = {
    spend:   spark?.spend?.delta_pct_yoy,
    leads:   spark?.leads?.delta_pct_yoy,
    cpl:     spark?.cpl?.delta_pct_yoy,
    deals:   spark?.deals?.delta_pct_yoy,
    revenue: spark?.revenue?.delta_pct_yoy,
  };

  const sheetSource = { label: "BHAG Sheet", tone: "cobalt",
        hint: "Monthly actuals from the '2026 Indie Law NEW' tab of your BHAG / Monthly Goals sheet · synced every 6h" };

  const bigNum = (v, fmt) => v == null ? <span className="opacity-40">—</span> : fmt(v);

  return (
    <div data-testid={DASHBOARD.page}>
      <TitleBlock
        eyebrow={lastFullMonthLabel ? `Last completed month · ${lastFullMonthLabel}` : `Last completed month`}
        title={lastFullMonthName ? `${lastFullMonthName} at a glance` : "Last completed month"}
        subtitle={lastFullMonthLabel
          ? `Each tile shows ${lastFullMonthName}'s actuals with a MoM delta vs the prior full month. The trend line under each tile puts ${lastFullMonthName} in context alongside the rest of the year. Current month is excluded — the team takes several days to close and verify.`
          : "No completed months yet in this year. Come back after February."}
      />

      <div className="p-6 md:p-8 space-y-8">
        {/* Last-full-month KPI grid — 6 tiles.
            Spend, CPL, and ROAS are split by platform (Google + Meta) using
            values sourced DIRECTLY from the BHAG sheet's per-platform rows. */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="ytd-kpi-grid">
          <PlatformSplitTile testid="ytd-spend" title="Ad Spend by platform"
            monthName={lastFullMonthName} platformRoas={platformRoas}
            metricKey="spend" format={fmtMoney}
            yFormat={(v) => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${Math.round(v)}`}
            source={{ label: "BHAG Sheet", tone: "cobalt",
                     hint: "Google Ad Spend Actual (BHAG row 42) and Meta Ad Spend Actual (BHAG row 53) — per-platform monthly totals." }} />
          <MonthTile testid="ytd-leads" label="Leads · calls booked"
            value={bigNum(lfm.leads, (v) => fmtNumber(v, 0))}
            source={sheetSource} monthName={lastFullMonthName}
            momPct={mom.leads} yoyPct={yoy.leads}
            yFormat={(v) => Math.round(v).toLocaleString()}
            spark={spark?.leads?.series} />
          <PlatformSplitTile testid="ytd-cpl" title="CPL by platform"
            monthName={lastFullMonthName} platformRoas={platformRoas}
            metricKey="cpl" format={fmtMoney} invertDelta
            yFormat={(v) => `$${Math.round(v)}`}
            source={{ label: "BHAG Sheet", tone: "cobalt",
                     hint: "Google CPL Actual (BHAG row 26) and Meta CPL Actual (BHAG row 45) — read directly from the sheet (not computed)." }} />
          <MonthTile testid="ytd-deals" label="Closed Deals"
            value={bigNum(lfm.deals, (v) => fmtNumber(v, 0))}
            source={sheetSource} monthName={lastFullMonthName}
            momPct={mom.deals} yoyPct={yoy.deals}
            yFormat={(v) => Math.round(v).toLocaleString()}
            spark={spark?.deals?.series} />
          <MonthTile testid="ytd-revenue" label="Revenue"
            value={<span className="text-evergreen">{bigNum(lfm.revenue, fmtMoney)}</span>}
            source={sheetSource} monthName={lastFullMonthName}
            momPct={mom.revenue} yoyPct={yoy.revenue}
            yFormat={(v) => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${Math.round(v)}`}
            spark={spark?.revenue?.series} />
          <PlatformSplitTile testid="ytd-roas" title="ROAS by platform"
            monthName={lastFullMonthName} platformRoas={platformRoas}
            metricKey="roas" format={(v) => `${v}×`}
            yFormat={(v) => `${v.toFixed(1)}×`}
            source={{ label: "BHAG Sheet", tone: "cobalt",
                     hint: "Google Ad ROAS Actual (BHAG row 43) and Meta Ad ROAS Actual (BHAG row 54) — read directly from the sheet." }} />
        </div>


        {/* Lead sources over time — extracted to its own component. */}
        <LeadSourcesSection />

        <Callout title="Sources">
          All metric values above are last-full-month actuals from the <strong>BHAG / Monthly Goals sheet</strong> (tab <em>2026 Indie Law NEW</em>). The Lead sources over time chart classifies HubSpot deals with our Meta-aware channel classifier. The current month is deliberately excluded from every tile, spark, and chart on this page.
        </Callout>

        {/* Monthly spend · trailing 12 months — sourced DIRECTLY from BHAG
            Google Ad Spend Actual (row 42) + Meta Ad Spend Actual (row 53),
            same numbers Joey uses to run the business. Current partial month
            is filtered out. */}
        {(() => {
          const monthlyRows = (platformRoas?.rows || [])
            .filter((r) => !r.is_partial)
            .filter((r) => (r.google_spend || 0) + (r.meta_spend || 0) > 0)
            .map((r) => ({
              period: r.period,
              google_spend: r.google_spend || 0,
              meta_spend:   r.meta_spend   || 0,
            }))
            .slice(-12);
          if (monthlyRows.length === 0) return null;
          return (
            <div className="bg-warm-tint border-t-4 border-cobalt p-6" data-testid={DASHBOARD.trendChart}>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-xl font-bold text-navy">Monthly spend · trailing 12 months</h2>
                <SourceBadge label="BHAG Sheet · Google + Meta" tone="cobalt"
                  hint="Google Ad Spend Actual (row 42) + Meta Ad Spend Actual (row 53) per month. Current partial month excluded."
                  testid="trend-source" />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyRows}>
                  <defs>
                    <linearGradient id="g_spend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1D57A5" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#1D57A5" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="m_spend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F58518" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#F58518" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#141B4D" strokeOpacity={0.1} vertical={false} />
                  <XAxis dataKey="period" tick={{ fill: "#101820", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#101820", fontSize: 11 }}
                         tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${Math.round(v)}`} />
                  <Tooltip contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "8px 12px" }}
                           itemStyle={{ color: "#F2E9DB" }}
                           labelStyle={{ color: "#FFD100", fontWeight: 700, marginBottom: 4 }}
                           formatter={(v) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#101820" }} />
                  <Area type="monotone" dataKey="google_spend" name="Google Ads" stroke="#1D57A5" fill="url(#g_spend)" strokeWidth={2} />
                  <Area type="monotone" dataKey="meta_spend" name="Meta Ads" stroke="#F58518" fill="url(#m_spend)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
